window.theme = window.theme || {};

/**
 * Ref-counted busy state: disables checkout immediately, restores when all mutations finish.
 */
window.theme.cartDrawerBusy = {
  depth: 0,

  begin() {
    this.depth += 1;
    if (this.depth !== 1) return;

    const checkout = document.getElementById("CartDrawer-Checkout");
    if (checkout) checkout.disabled = true;

    const shell = document.getElementById("CartDrawer-Form-Shell");
    const form = document.getElementById("CartDrawer-Form");
    const overlay = document.getElementById("CartDrawer-Form-Loading");
    if (shell) shell.classList.add("cart-drawer-form--updating");
    if (form) form.setAttribute("aria-busy", "true");
    if (overlay) {
      overlay.hidden = false;
      overlay.setAttribute("aria-hidden", "false");
    }
  },

  end(cartState) {
    this.depth = Math.max(0, this.depth - 1);
    if (this.depth > 0) return;

    const shell = document.getElementById("CartDrawer-Form-Shell");
    const form = document.getElementById("CartDrawer-Form");
    const overlay = document.getElementById("CartDrawer-Form-Loading");
    if (shell) shell.classList.remove("cart-drawer-form--updating");
    if (form) form.removeAttribute("aria-busy");
    if (overlay) {
      overlay.hidden = true;
      overlay.setAttribute("aria-hidden", "true");
    }

    const checkout = document.getElementById("CartDrawer-Checkout");
    if (!checkout) return;

    const n = cartState && typeof cartState.item_count === "number" ? cartState.item_count : null;
    checkout.disabled = n !== null ? n === 0 : false;
  },
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        // Prefer the empty-state panel only when it still exists. After AJAX add-to-cart,
        // .drawer__inner may lose is-empty while <cart-drawer> still has is-empty — then
        // .drawer__inner-empty is gone and querySelector returns null.
        const drawerInnerEmpty = this.querySelector(".drawer__inner-empty");
        const cartDrawerPanel = document.getElementById("CartDrawer");
        const drawerInner = this.querySelector(".drawer__inner");
        const containerToTrapFocusOn = drawerInnerEmpty || cartDrawerPanel || drawerInner || this;
        const focusElement = drawerInner || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  /**
   * When the cart still has lines, patch only list + totals (not header, promo, or checkout CTA).
   * When the cart is empty, replace the whole drawer inner so empty-state markup appears.
   */
  getDomUpdatesAfterCartChange(parsedState) {
    if (!parsedState || parsedState.item_count === 0) {
      return [
        { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
        { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
      ];
    }
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: "#CartDrawer-CartItems" },
      { id: "CartDrawer", section: "cart-drawer", selector: "#cart-progress" },
      { id: "CartDrawer", section: "cart-drawer", selector: ".cart-drawer__footer" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
    ];
  }

  /** Header item count + checkout (not re-patched with cart lines to avoid losing disabled state). */
  syncCartDrawerChrome(parsedState) {
    const n = Number(parsedState && parsedState.item_count);
    if (Number.isNaN(n)) return;
    const itemCountEl = document.getElementById("CartDrawer-ItemCount");
    if (itemCountEl) {
      const fmt = itemCountEl.getAttribute("data-count-format") || "([count] items)";
      itemCountEl.textContent = fmt.replace("[count]", String(n));
      itemCountEl.classList.toggle("hidden", n === 0);
    }
    const checkout = document.getElementById("CartDrawer-Checkout");
    if (checkout) {
      checkout.disabled = n === 0;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
